import { AuthMethod } from '@infotech-soe/merps/types/session';
import { tg } from '@infotech-soe/metaschema';

import MerpsApiProvider from '../../../infrastructure/providers/pb-adapter/MerpsApiProvider';
import { STORAGE_KEYS } from '../../../services/configuration/common';
import LocalConfigurationService from '../../../services/configuration/LocalConfigurationService';
import ConnectionService from '../../../services/connection/ConnectionService';
import connection from '../../slices/connection';
import session from '../../slices/session';
import {
  apiCall,
  createAppListener,
  onceInit,
  postDispatch,
} from '../listenerHelpers';

const connectionListener = createAppListener();

const apiProvider = new MerpsApiProvider();
const localConfigurationService = new LocalConfigurationService();
const connectionService = new ConnectionService();

connectionListener.startListening(
  onceInit((_state, dispatch) => {
    apiProvider.rpc.onConnectionStatusChanged.addListener(connected => {
      dispatch(connection.setConnectionStatus({ connected }));
    });
  })
);

const getConnectResult = () => ({
  connectionId: connectionService.connectionId,
  connected: connectionService.isConnected(),
});

connectionListener.startListening(
  apiCall(
    {
      request: connection.connect,
      success: connection.connectSuccess,
      failure: connection.connectFailure,
    },
    async () => {
      if (connectionService.isConnected()) {
        return getConnectResult();
      }

      await connectionService.connect();

      return getConnectResult();
    }
  )
);

connectionListener.startListening(
  apiCall(
    {
      request: connection.connectAndSignIn,
      success: connection.connectAndSignInSuccess,
      failure: connection.connectAndSignInFailure,
    },
    async (payload, _state, dispatch) => {
      const login = localConfigurationService.getStoredUser();
      const application = localConfigurationService.getDefaultApplication();
      localStorage.setItem('app', application);

      if (connectionService.isConnected()) {
        dispatch(session.bindStateToExistingSession({ login, application }));
        return payload;
      }

      await connectionService.connect();
      return payload;
    }
  )
);

connectionListener.startListening(
  postDispatch(connection.connectSuccess, ({ connected }, _state, dispatch) => {
    if (connected) {
      dispatch(session.loadFeatures());
    }
  })
);

connectionListener.startListening(
  postDispatch(
    connection.connectAndSignInSuccess,
    ({ isReconnection }, _state, dispatch) => {
      const userName = localConfigurationService.getStoredUser();
      const password = localConfigurationService.getStoredPassword();
      const application = localConfigurationService.getDefaultApplication();
      const preferredMethod =
        localConfigurationService.getPreferredAuthMethod();

      dispatch(connection.connectSuccess(getConnectResult()));

      const isValidCreds =
        tg.isString(application) &&
        tg.isString(userName) &&
        tg.isString(password);

      const isLoggedIn = sessionStorage.getItem(
        STORAGE_KEYS.STORED_CREDENTIALS
      );

      if (isValidCreds && isLoggedIn) {
        const method = preferredMethod ?? AuthMethod.LOGIN;
        const payload = { userName, password, application } as const;

        if (isReconnection) {
          dispatch(
            session.reconnection({
              type:
                method === AuthMethod.WINDOWS_AD
                  ? AuthMethod.WINDOWS_AD
                  : AuthMethod.LOGIN,
              ...payload,
            })
          );
        } else if (connectionService.isConnected()) {
          dispatch(
            session.signIn({
              type:
                method === AuthMethod.WINDOWS_AD
                  ? AuthMethod.WINDOWS_AD
                  : AuthMethod.LOGIN,
              ...payload,
            })
          );
        }
      }
    }
  )
);

connectionListener.startListening(
  postDispatch(connection.setConnectionStatus, ({ connected }) => {
    connectionService.setConnected(connected);
  })
);

export default connectionListener.middleware;
